import maplibregl from 'maplibre-gl';
import { objectInOrder, parseArtifactDescription } from './mapUtils';
import { ARTIFACTS_FIELD_ORDER, SOURCE_IDS } from './mapConstants';
import { SimpleObject } from '../../types';

// MAP ADD EVENT UTILITIES ==================
export const addMoveMouseEvent = (map: maplibregl.Map, setPointerCoordinates: any) => {
  map.on('mousemove', (e: any) => {
    // e.lngLat is the longitude, latitude geographical position of the event
    setPointerCoordinates(
      `X: ${e.point.x}, Y: ${e.point.y} <--> Lng: ${e.lngLat.lng}, Lat: ${e.lngLat.lat}`
    );
  });
};

export const addClickEvent = (
  map: maplibregl.Map,
  layersName: string[],
  callback: (e: any, features: any) => void
) => {
  // Escuchar eventos de clic en el mapa
  map.on('click', function (e) {
    // Consultar las características (features) en la posición del clic
    var features = map.queryRenderedFeatures(e.point, {
      layers: layersName
    });

    // Si se ha hecho clic en una línea, ejecutar la función de manejo de clic
    if (features.length > 0) {
      callback(e, features);
    }
  });
};

// MAP 'CLICK' ENVENTS =================
export const deselectAllFeatures = (
  map: { current: maplibregl.Map | null },
  uniqueIds: string[] // Array con los IDs de todas las capas a deseleccionar
) => {
  uniqueIds.forEach((uniqueId) => {
    if (!map.current) return;

    const source = map.current.getSource(uniqueId) as maplibregl.GeoJSONSource;

    if (
      !source ||
      !source?._data ||
      typeof source?._data !== 'object' ||
      !Array.isArray((source._data as any).features)
    )
      return;

    const allFeatures = (source._data as any).features;

    allFeatures.forEach((feature: any) => {
      map.current?.setFeatureState(
        { source: uniqueId, id: feature.properties.uniqueFeatureId },
        { selected: false }
      );
    });
  });
};

export const selectFullElectricFeederPolygon = (
  map: { current: maplibregl.Map | null },
  electricFeeders: SimpleObject,
  clickedFeature: any,
  callback?: any
) => {
  if (!map.current) {
    return;
  }

  // GIVE DATA OF THE CURRENT ELECTRIC FEEDER TO THE CALLBACK
  if (callback) {
    const alimentadorId =
      clickedFeature._vectorTileFeature?.properties?.alimentadorId ??
      clickedFeature.properties?.alimentadorId;

    const findAlimentadorData =
      Array.isArray(electricFeeders) &&
      electricFeeders.find(
        (obj: SimpleObject) => Number(obj.alimentadorId) === Number(alimentadorId)
      );

    if (findAlimentadorData) {
      const orderedObject = objectInOrder(
        findAlimentadorData.descripcionArtefacto,
        ARTIFACTS_FIELD_ORDER.Alimentador,
        ['demanda_minima_kw', 'demanda_maxima_kw']
      );

      callback(orderedObject);
    } else {
      callback({
        Mensaje: 'ALIMENTADOR NO ENCONTRADO',
        alimentadorId
      });
    }
  }

  deselectAllFeatures(map, [SOURCE_IDS.lines, SOURCE_IDS.points]);

  // SELECT FULL ELECTTRIC FEEDER
  const source = map.current.getSource(SOURCE_IDS.polygon) as maplibregl.GeoJSONSource;

  if (
    !source ||
    !source?._data ||
    typeof source?._data !== 'object' ||
    !Array.isArray((source._data as any).features)
  )
    return;

  const allFeatures = (source._data as any).features;
  const clickedFeatureAlimentadorId = clickedFeature.properties.alimentadorId;

  allFeatures.forEach((feature: any) => {
    if (!map.current || !feature.properties) return;

    if (feature.properties?.alimentadorId === clickedFeatureAlimentadorId) {
      map.current.setFeatureState(
        {
          source: SOURCE_IDS.polygon,
          id: feature.properties.uniqueFeatureId
        },
        {
          selected: true
        }
      );
    } else {
      map.current.setFeatureState(
        {
          source: SOURCE_IDS.polygon,
          id: feature.properties.uniqueFeatureId
        },
        {
          selected: false
        }
      );
    }
  });
};

export const selectFullElectricFeederLines = (
  map: { current: maplibregl.Map | null },
  electricFeeders: SimpleObject,
  clickedFeature: any,
  callback?: any
) => {
  if (!map.current) {
    return;
  }

  // GIVE DATA OF THE CURRENT ELECTRIC FEEDER TO THE CALLBACK
  if (callback) {
    const descVec = parseArtifactDescription(
      clickedFeature._vectorTileFeature?.properties?.descripcionArtefacto ??
        clickedFeature.properties?.descripcionArtefacto
    );
    const alimentadorId = descVec.alimentador_id;

    const findAlimentadorData =
      Array.isArray(electricFeeders) &&
      electricFeeders.find(
        (obj: SimpleObject) => Number(obj.alimentadorId) === Number(alimentadorId)
      );

    if (findAlimentadorData?.descripcionArtefacto) {
      const orderedObject = objectInOrder(
        findAlimentadorData.descripcionArtefacto,
        ARTIFACTS_FIELD_ORDER.Alimentador,
        ['demanda_minima_kw', 'demanda_maxima_kw']
      );

      callback(orderedObject);
    } else {
      callback({
        Mensaje: 'ALIMENTADOR NO ENCONTRADO',
        alimentadorId
      });
    }
  }

  deselectAllFeatures(map, [SOURCE_IDS.polygon, SOURCE_IDS.points]);

  // SELECT FULL ELECTTRIC FEEDER
  const source = map.current.getSource(SOURCE_IDS.lines) as maplibregl.GeoJSONSource;

  if (
    !source ||
    !source?._data ||
    typeof source?._data !== 'object' ||
    !Array.isArray((source._data as any).features)
  )
    return;

  const allFeatures = (source._data as any).features;
  const clickedDesc = parseArtifactDescription(clickedFeature.properties?.descripcionArtefacto);
  const clickedFeatureAlimentadorId = clickedDesc.alimentador_id;

  allFeatures.forEach((feature: any) => {
    if (!map.current || !feature.properties) return;

    const featureDesc = parseArtifactDescription(feature.properties.descripcionArtefacto);

    if (featureDesc.alimentador_id === clickedFeatureAlimentadorId) {
      map.current.setFeatureState(
        {
          source: SOURCE_IDS.lines,
          id: feature.properties.uniqueFeatureId
        },
        {
          selected: true
        }
      );
    } else {
      map.current.setFeatureState(
        {
          source: SOURCE_IDS.lines,
          id: feature.properties.uniqueFeatureId
        },
        {
          selected: false
        }
      );
    }
  });
};

export const selectArtifact = (
  map: { current: maplibregl.Map | null },
  clickedFeature: any,
  callback?: any
) => {
  if (!map.current) {
    return;
  }

  if (callback) {
    const rawProps = clickedFeature._vectorTileFeature?.properties || clickedFeature.properties || {};
    const objectData = parseArtifactDescription(rawProps.descripcionArtefacto);

    let result: SimpleObject;
    if (ARTIFACTS_FIELD_ORDER[objectData.artefacto]) {
      result = objectInOrder(objectData, ARTIFACTS_FIELD_ORDER[objectData.artefacto], [
        'demanda_minima_kw',
        'demanda_maxima_kw'
      ]);
    } else {
      const { artefacto, empresa_id, alimentador_id, ...rest } = objectData;
      result = { artefacto, empresa_id, alimentador_id, ...rest };
    }

    result.descripcionArtefacto = objectData;
    result.id = rawProps.id ?? objectData.poste_id ?? rawProps.poste_id;
    callback(result);
  }

  deselectAllFeatures(map, [SOURCE_IDS.polygon, SOURCE_IDS.points]);

  // HIGHLIGHT THE CLICKED LINE (only relevant for line artifacts that have tramo_id)
  const clickedDesc = parseArtifactDescription(clickedFeature.properties?.descripcionArtefacto);
  const clickedFeatureTramoId = clickedDesc.tramo_id;

  if (!clickedFeatureTramoId) return;

  const source = map.current.getSource(SOURCE_IDS.lines) as maplibregl.GeoJSONSource;

  if (
    !source ||
    !source?._data ||
    typeof source?._data !== 'object' ||
    !Array.isArray((source._data as any).features)
  )
    return;

  const allFeatures = (source._data as any).features;

  allFeatures.forEach((feature: any) => {
    if (!map.current || !feature.properties) return;

    const featureDesc = parseArtifactDescription(feature.properties.descripcionArtefacto);

    if (featureDesc.tramo_id === clickedFeatureTramoId) {
      map.current.setFeatureState(
        {
          source: SOURCE_IDS.lines,
          id: feature.properties.uniqueFeatureId
        },
        {
          selected: true
        }
      );
    } else {
      map.current.setFeatureState(
        {
          source: SOURCE_IDS.lines,
          id: feature.properties.uniqueFeatureId
        },
        {
          selected: false
        }
      );
    }
  });
};
